import React, {
  forwardRef,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import {
  Alert,
  Platform,
  Pressable,
  View,
  FlatList,
  Image,
  TouchableOpacity,
} from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { Text } from '@constants/Themed';
import { SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import { useDispatch } from 'react-redux';
import { H5, YStack, Spinner } from 'tamagui';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialIcons } from '@expo/vector-icons';
import globalStyles from '@stylesheet';
import { BillingInfo } from '../types/types';
import * as Stripe from '@stripe/stripe-react-native';
import apis from '@api/api';
import { selectUser } from '@reduxLocal/selectors/userSelectors';

/**
 * StripeUpgradeComponent.native - Native Stripe subscription plans component
 * 
 * This component is specifically for native platforms (iOS/Android).
 * It uses the same logic as the web version but with native-specific styling
 * similar to the IAP component (LinearGradient cards, native UI elements).
 */

interface StripeUpgradeComponentProps {
  userPlan: any;
  selectedGroup?: string;
  onGroupChange?: (group: 'monthly' | 'annual') => void;
  planGroups?: PlanGroup[];
  planIsLoading?: boolean;
}

type StripeUpgradeComponentHandle = {
  openPaymentModal: () => void;
};

// Types for better type safety
interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  default_price: {
    id: string;
    unit_amount: number | null;
    currency: string;
    recurring: {
      interval: 'month' | 'year';
      interval_count: number;
    } | null;
    type: 'recurring' | 'one_time';
    billing_scheme: 'per_unit' | 'tiered';
  };
  features: Array<{ name: string }>;
  marketing_features: Array<{ name: string }>;
  metadata: Record<string, string>;
  images: string[];
}

interface PlanGroup {
  title: string;
  plans: StripeProduct[];
}

const StripeUpgradeComponent = forwardRef<StripeUpgradeComponentHandle, StripeUpgradeComponentProps>(
  ({ userPlan, selectedGroup = 'monthly', onGroupChange, planGroups = [], planIsLoading = false }, ref) => {
    const dispatch = useDispatch();
    const theme = themeSelector();
    const [selectedItem, setSelectedItem] = useState<StripeProduct | null>(null);
    const queryClient = useQueryClient();
    const currentPlan: BillingInfo = queryClient.getQueryData(['subscriptionPlan']);

    const { initPaymentSheet, presentPaymentSheet } = Stripe.useStripe();
    const [paymentInProgress, setPaymentInProgress] = useState(false);
    const user = selectUser();

    // Calculate total plans for logging
    const totalPlans = useMemo(() => planGroups.reduce((sum, g) => sum + (g?.plans?.length || 0), 0), [planGroups]);

    console.log('💳 StripeUpgradeComponent.native RENDERED:', {
      planGroupsLength: planGroups.length,
      totalPlans,
      selectedGroup,
      planIsLoading,
    });

    // Utility functions
    const formatPrice = (amount: number | null, currency: string) => {
      if (amount === null || amount === undefined) {
        return 'Contact Sales';
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(amount / 100);
    };

    const getPlanFeatures = (product: StripeProduct) => {
      return product.marketing_features?.length > 0
        ? product.marketing_features
        : product.features || [];
    };

    const isCurrentPlan = (product: StripeProduct) => {
      return currentPlan?.product?.id === product.id;
    };

    // Fetch subscription payment sheet params
    const fetchSubscriptionPaymentSheet = useCallback(async (product: StripeProduct) => {
      try {
        if (!product.default_price?.id) {
          throw new Error('Price ID not found for this product');
        }

        const billingId = user?.billing_info?.stripe?.billingId;
        const res = await apis.createSubscriptionPaymentSheet({
          priceId: product.default_price.id,
          productId: product.id,
          billingId,
        });
        return res;
      } catch (error) {
        console.error('Error fetching subscription payment sheet params:', error);
        throw error;
      }
    }, [user]);

    // Initialize and present payment sheet for subscription
    const initializeAndPresentPaymentSheet = useCallback(async (product: StripeProduct) => {
      try {
        setPaymentInProgress(true);
        dispatch({ type: SHOW_LOADING_MODAL });

        console.log('🔄 Starting subscription payment flow for:', product.name);

        const { setupIntent, ephemeralKey, customer, publishableKey } =
          await fetchSubscriptionPaymentSheet(product);

        console.log('✅ Payment sheet params fetched');

        const { error: initError } = await initPaymentSheet?.({
          merchantDisplayName: 'SmartSaaS Ltd',
          customerId: customer,
          appearance: {
            colors: {
              primary: theme ? '#3b82f6' : '#60a5fa',
            },
          },
          customerEphemeralKeySecret: ephemeralKey,
          setupIntentClientSecret: setupIntent,
          allowsDelayedPaymentMethods: true,
          // Remove returnURL for setup intents - they don't need it
          // returnURL: 'smartsaas://payment-return',
        });

        if (initError) {
          console.error('❌ Payment sheet initialization error:', initError);
          throw new Error(initError.message);
        }

        console.log('✅ Payment sheet initialized, hiding loading modal and presenting sheet...');

        // Hide loading modal so user can interact with payment sheet
        dispatch({ type: HIDE_LOADING_MODAL });

        // Present payment sheet with timeout
        console.log('📱 Presenting payment sheet...');
        console.log('📱 presentPaymentSheet function:', typeof presentPaymentSheet);

        if (!presentPaymentSheet) {
          throw new Error('Payment sheet not available. Please ensure Stripe is properly initialized.');
        }

        let presentResult: { error?: any };
        try {
          presentResult = await Promise.race([
            presentPaymentSheet(),
            new Promise<{ error: any }>((_, reject) =>
              setTimeout(() => reject(new Error('Payment sheet timeout after 60 seconds')), 60000)
            )
          ]);
          console.log('📱 Payment sheet promise resolved:', presentResult);
        } catch (timeoutError) {
          console.error('❌ Payment sheet timeout or error:', timeoutError);
          throw timeoutError;
        }

        if (presentResult?.error) {
          console.error('❌ Payment sheet presentation error:', presentResult.error);
          if (presentResult.error.code === 'Canceled' || presentResult.error.code === 'Failed') {
            console.log('ℹ️ User canceled or payment failed');
            setPaymentInProgress(false);
            dispatch({
              type: SHOW_AUTH_MODAL,
              data: {
                content: presentResult.error.code === 'Canceled'
                  ? 'Payment was canceled'
                  : 'Payment failed. Please try again.',
                type: 'info',
              },
            });
            return;
          }
          throw new Error(presentResult.error.message || 'Payment sheet presentation failed');
        }

        console.log('✅ Payment sheet completed successfully, payment method collected');

        // Show loading while creating subscription
        dispatch({ type: SHOW_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'Creating subscription...', type: 'info' },
        });

        // Small delay to ensure setup intent is fully processed
        await new Promise(resolve => setTimeout(resolve, 500));

        console.log('🔄 Creating subscription...');

        // Create subscription using changeSubscription endpoint
        const subscriptionResult = await apis.changeSubscription({
          productId: product.id,
          priceId: product.default_price.id,
          prorationBehavior: 'none',
        });

        console.log('📦 Subscription result:', subscriptionResult);

        if (subscriptionResult?.success) {
          // Refetch subscription data
          console.log('🔄 Refetching subscription data...');
          await queryClient.refetchQueries({ queryKey: ['subscriptionPlan'] });

          dispatch({ type: HIDE_LOADING_MODAL });
          dispatch({
            type: SHOW_AUTH_MODAL,
            data: {
              content: `Successfully subscribed to ${product.name}! Welcome to SmartSaaS Premium.`,
              type: 'success',
            },
          });
          setSelectedItem(null);
          console.log('✅ Subscription created successfully');
        } else {
          throw new Error(subscriptionResult?.error || 'Failed to create subscription');
        }
      } catch (error) {
        console.error('❌ Payment flow error:', error);
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: error.message || 'Payment failed. Please try again.',
            type: 'error',
          },
        });
        setPaymentInProgress(false);
      } finally {
        // Ensure loading modal is hidden
        dispatch({ type: HIDE_LOADING_MODAL });
      }
    }, [fetchSubscriptionPaymentSheet, initPaymentSheet, presentPaymentSheet, dispatch, queryClient, theme, user]);

    const openPaymentModal = useCallback(() => {
      if (selectedItem && !paymentInProgress) {
        initializeAndPresentPaymentSheet(selectedItem);
      }
    }, [selectedItem, paymentInProgress, initializeAndPresentPaymentSheet]);

    // Expose ref methods
    React.useImperativeHandle(ref, () => ({
      openPaymentModal,
    }));

    // Get all plans for the selected group
    const allPlans = useMemo(() => {
      const matchingGroup = planGroups.find(group =>
        group.title.toLowerCase().includes(selectedGroup)
      );

      if (matchingGroup?.plans?.length > 0) {
        return matchingGroup.plans;
      }

      // Fallback: if no match, try to get any available plans
      if (planGroups.length > 0) {
        const monthlyGroup = planGroups.find(g => g.title.toLowerCase().includes('monthly'));
        const annualGroup = planGroups.find(g => g.title.toLowerCase().includes('annual'));
        const fallbackGroup = monthlyGroup || annualGroup || planGroups[0];

        if (fallbackGroup?.plans?.length > 0) {
          return fallbackGroup.plans;
        }
      }

      return [];
    }, [planGroups, selectedGroup]);

    // Filter out free plan if user has a higher-tier plan
    const currentPlans = useMemo(() => {
      if (!currentPlan?.product?.name) return allPlans;

      const userPlanName = currentPlan.product.name.toLowerCase();
      const isOnFreePlan = userPlanName.includes('free') ||
        userPlanName.includes('starter') ||
        userPlanName.includes('exempt');

      if (isOnFreePlan) return allPlans;

      return allPlans.filter(plan => {
        const planName = plan.name.toLowerCase();
        return !planName.includes('free') &&
          !planName.includes('starter') &&
          !planName.includes('exempt');
      });
    }, [allPlans, currentPlan?.product?.name]);

    // Render subscription item with native styling (similar to IAP component)
    const RenderSubscriptionItem = ({ item, index }: { item: StripeProduct; index: number }) => {
      const isSelected = selectedItem?.id === item.id;
      const isCurrent = isCurrentPlan(item);
      const features = getPlanFeatures(item);
      const price = formatPrice(item.default_price.unit_amount, item.default_price.currency);
      const interval = item.default_price.recurring?.interval === 'year' ? 'year' : 'month';

      // Theme-aware gradient border colors matching native IAP component
      const cardGradientBorderColors = theme
        ? (['#3b82f6', '#a855f7'] as const)
        : (['#3b82f6', '#a855f7'] as const);

      const activeBadgeGradientColors = theme
        ? (['rgba(245, 101, 101, 0.95)', 'rgba(239, 68, 68, 1)'] as const)
        : (['rgba(248, 113, 113, 0.9)', 'rgba(239, 68, 68, 0.95)'] as const);

      return (
        <LinearGradient
          colors={cardGradientBorderColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={[
            {
              flex: 1,
              minWidth: 300,
              borderRadius: 20,
              padding: isSelected ? 4 : 3, // Thicker border when selected
              elevation: isSelected ? 12 : 8,
              shadowColor: theme ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.4)',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: theme ? (isSelected ? 0.4 : 0.3) : (isSelected ? 0.5 : 0.4),
              shadowRadius: 12,
              overflow: 'visible',
            },
          ]}
        >
          {isCurrent && (
            <LinearGradient
              colors={activeBadgeGradientColors}
              style={{
                position: 'absolute',
                top: 10,
                left: -35,
                paddingHorizontal: 40,
                paddingVertical: 5,
                transform: [{ rotate: '-45deg' }],
                borderRadius: 8,
                elevation: 8,
                shadowColor: theme ? 'rgba(245, 101, 101, 0.4)' : 'rgba(239, 68, 68, 0.5)',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: theme ? 0.4 : 0.5,
                shadowRadius: 12,
                zIndex: 10,
              }}
            >
              <Text style={{ color: 'white', fontWeight: '700', fontSize: 12, letterSpacing: 1 }}>ACTIVE</Text>
            </LinearGradient>
          )}

          <View
            style={{
              flex: 1,
              borderRadius: 17,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
              overflow: 'hidden',
            }}
          >
            <Pressable
              disabled={isCurrent}
              onPress={() => setSelectedItem((prev) => (prev?.id === item.id ? null : item))}
              style={[
                {
                  paddingBottom: 30,
                  flex: 1,
                  maxWidth: 350,
                  padding: 20,
                }
              ]}
            >
              {/* Plan Image */}
              {item.images?.[0] && (
                <View style={{ alignItems: 'center', marginBottom: 16 }}>
                  <View style={{
                    width: 80,
                    height: 80,
                    borderRadius: 20,
                    backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'rgba(248, 250, 252, 0.8)',
                    borderWidth: 2,
                    borderColor: isSelected ? '#3b82f6' : 'rgba(0, 0, 0, 0.1)',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}>
                    <Image source={{ uri: item.images[0] }} style={{ width: 80, height: 80 }} />
                  </View>
                </View>
              )}

              {/* Plan Name */}
              <YStack flex={1}>
                <H5 style={{ fontWeight: "bold", textAlign: 'center', color: theme ? "#1e293b" : "#f1f5f9", marginBottom: 8 }}>
                  {item.name}
                </H5>

                {/* Price */}
                <Text style={{ textAlign: "center", color: theme ? "#1e293b" : "#f1f5f9", fontSize: 24, fontWeight: '700', marginBottom: 8 }}>
                  {price !== 'Contact Sales' && item.default_price.unit_amount !== null
                    ? `${price}/${interval}`
                    : price}
                </Text>

                {/* Description */}
                <Text style={{ paddingHorizontal: 20, fontSize: 14, color: theme ? "#64748b" : "#94a3b8", textAlign: 'center', marginBottom: 16 }}>
                  {item.description}
                </Text>

                {/* Features */}
                {features.length > 0 && (
                  <View style={{ marginBottom: 16 }}>
                    <Text style={{
                      color: theme ? "#1e293b" : "#f1f5f9",
                      fontSize: 16,
                      fontWeight: '600',
                      marginBottom: 12,
                      textAlign: 'center'
                    }}>
                      Features
                    </Text>
                    {features.slice(0, 4).map((feature, idx) => (
                      <View key={idx} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8, paddingHorizontal: 20 }}>
                        <Text style={{ color: '#22c55e', marginRight: 10, fontSize: 16, fontWeight: '600' }}>✓</Text>
                        <Text style={{ color: theme ? "#64748b" : "#94a3b8", fontSize: 14, flex: 1 }}>
                          {feature.name}
                        </Text>
                      </View>
                    ))}
                    {features.length > 4 && (
                      <Text style={{ color: theme ? "#64748b" : "#94a3b8", fontSize: 12, textAlign: 'center', fontStyle: 'italic', marginTop: 8 }}>
                        +{features.length - 4} more features
                      </Text>
                    )}
                  </View>
                )}
              </YStack>
            </Pressable>

            {/* Action Button - Full Width */}
            {isCurrent ? (
              <TouchableOpacity
                style={[globalStyles.modernActionButton, theme ? globalStyles.modernActionRed : globalStyles.modernActionRedDark, {
                  marginHorizontal: 0,
                  marginBottom: 0,
                  width: '100%',
                  borderRadius: 0,
                  borderBottomLeftRadius: 17,
                  borderBottomRightRadius: 17
                }]}
                onPress={() => {
                  dispatch({
                    type: SHOW_AUTH_MODAL,
                    data: { content: 'Manage your subscription in the Stripe customer portal', type: 'info' },
                  });
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <MaterialIcons name="settings" size={20} color="white" />
                  <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>Manage Subscription</Text>
                </View>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, {
                  marginHorizontal: 0,
                  marginBottom: 0,
                  width: '100%',
                  borderRadius: 0,
                  borderBottomLeftRadius: 17,
                  borderBottomRightRadius: 17,
                  opacity: paymentInProgress ? 0.6 : 1,
                }]}
                disabled={paymentInProgress}
                onPress={() => {
                  if (isSelected) {
                    openPaymentModal();
                  } else {
                    setSelectedItem(item);
                  }
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  {paymentInProgress ? (
                    <Spinner size="small" color="white" />
                  ) : (
                    <MaterialIcons name={isSelected ? "payment" : "info-outline"} size={20} color="white" />
                  )}
                  <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                    {paymentInProgress ? 'Processing...' : (isSelected ? 'Continue to Payment' : 'Select Plan')}
                  </Text>
                </View>
              </TouchableOpacity>
            )}
          </View>
        </LinearGradient>
      );
    };

    // Loading component
    const LoadingStateComponent = () => (
      <View style={[globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { alignItems: 'center', padding: 24 }]}>
        <View style={{ alignItems: 'center', gap: 16 }}>
          <Spinner size="large" color="$blue10" />
          <Text style={{
            color: theme ? '#1e293b' : '#f1f5f9',
            fontSize: 16,
            fontWeight: '500'
          }}>
            Loading subscription plans...
          </Text>
        </View>
      </View>
    );

    // Empty state component
    const EmptyPlansComponent = () => (
      <View style={[globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { alignItems: 'center', padding: 24 }]}>
        <Text style={{
          color: theme ? '#1e293b' : '#f1f5f9',
          fontSize: 16,
          fontWeight: '600',
          marginBottom: 8,
          textAlign: 'center'
        }}>
          {planIsLoading ? 'Loading plans...' : `No ${selectedGroup} plans available`}
        </Text>
        <Text style={{
          color: theme ? '#64748b' : '#94a3b8',
          fontSize: 14,
          textAlign: 'center'
        }}>
          {planGroups.length === 0
            ? 'Please check back later for available subscription options'
            : `No ${selectedGroup} plans found. Try switching to ${selectedGroup === 'monthly' ? 'annual' : 'monthly'} plans.`
          }
        </Text>
      </View>
    );

    return (
      <View style={{ flex: 1, minHeight: 300, gap: 0 }}>
        {/* Monthly/Annual Toggle */}
        {planGroups.length > 0 && (
          <View style={{
            flexDirection: 'row',
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 41, 59, 0.9)',
            borderRadius: 12,
            padding: 4,
            marginHorizontal: 20,
            marginBottom: 16,
            borderWidth: 1,
            borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
          }}>
            {planGroups.map((group, index) => {
              const isActive = group.title.toLowerCase().includes(selectedGroup);
              return (
                <Pressable
                  key={index}
                  onPress={() => {
                    const newGroup = group.title.toLowerCase().includes('monthly') ? 'monthly' : 'annual';
                    onGroupChange?.(newGroup);
                  }}
                  style={{
                    flex: 1,
                    paddingHorizontal: 16,
                    paddingVertical: 10,
                    borderRadius: 8,
                    backgroundColor: isActive ? '#3b82f6' : 'transparent',
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: isActive ? 'white' : (theme ? '#64748b' : '#94a3b8'),
                    fontWeight: '600',
                    fontSize: 14,
                    textAlign: 'center'
                  }}>
                    {group.title}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        )}

        {/* Plans Section */}
        {planIsLoading ? (
          <LoadingStateComponent />
        ) : currentPlans && currentPlans.length > 0 ? (
          <FlatList
            data={currentPlans}
            centerContent
            renderItem={({ item, index }) => <RenderSubscriptionItem item={item} index={index} />}
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled
            ListEmptyComponent={EmptyPlansComponent}
            horizontal
            contentContainerStyle={{ gap: 10, paddingHorizontal: "10%", paddingVertical: 20 }}
            style={{ flex: 1, marginHorizontal: -20 }}
          />
        ) : (
          <EmptyPlansComponent />
        )}

        {/* Selected Item Details */}
        {selectedItem && (
          <Animatable.View useNativeDriver animation={"bounceIn"}>
            <View style={[globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { marginTop: 16 }]}>
              <View style={{ padding: 20, gap: 20 }}>
                {/* Selected Plan Header */}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <MaterialIcons name="workspace-premium" size={32} color={theme ? '#3b82f6' : '#60a5fa'} />
                  <View style={{ flex: 1 }}>
                    <Text style={{
                      color: theme ? '#1e293b' : '#f1f5f9',
                      fontSize: 20,
                      fontWeight: '700'
                    }}>
                      {selectedItem.name}
                    </Text>
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14
                    }}>
                      {selectedItem.default_price.unit_amount !== null
                        ? `${formatPrice(selectedItem.default_price.unit_amount, selectedItem.default_price.currency)}/${selectedItem.default_price.recurring?.interval}`
                        : formatPrice(selectedItem.default_price.unit_amount, selectedItem.default_price.currency)}
                    </Text>
                  </View>
                </View>

                {/* Primary Upgrade Button */}
                <TouchableOpacity
                  style={[globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, {
                    opacity: paymentInProgress ? 0.6 : 1,
                  }]}
                  disabled={paymentInProgress}
                  onPress={openPaymentModal}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {paymentInProgress ? (
                      <Spinner size="small" color="white" />
                    ) : (
                      <MaterialIcons name="upgrade" size={20} color="white" />
                    )}
                    <Text style={{ color: 'white', fontWeight: '700', fontSize: 16 }}>
                      {paymentInProgress ? 'Processing...' : `Upgrade to ${selectedItem.name}`}
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </View>
          </Animatable.View>
        )}
      </View>
    );
  }
);

export default StripeUpgradeComponent;

